import { TextInput, TextInputProps, StyleSheet, View, Text } from 'react-native';
import { forwardRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';

export interface InputProps extends TextInputProps {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<TextInput, InputProps>(({ label, error, hint, style, placeholderTextColor, ...props }, ref) => {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const styles = makeStyles(tokens, utils);

  // Calculate opacity for placeholder (80% = 0.8)
  const placeholderColor = placeholderTextColor || `${tokens.color.onSurface}CC`; // CC = 80% opacity in hex

  return (
    <View style={styles.container}>
      {label && <Text style={styles.label}>{label}</Text>}
      <TextInput
        ref={ref}
        style={[styles.input, error && styles.inputError, style].filter((s) => s != null && s !== '') as any}
        placeholderTextColor={placeholderColor}
        {...props}
      />
      {error && <Text style={styles.errorText}>{error}</Text>}
      {hint && !error && <Text style={styles.hintText}>{hint}</Text>}
    </View>
  );
});

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    container: {
      width: '100%',
      marginBottom: utils.spacing.md,
    },
    label: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
    },
    input: {
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.md,
      paddingHorizontal: utils.spacing.md,
      paddingVertical: utils.spacing.md,
      backgroundColor: tokens.color.surface,
      color: tokens.color.onSurface,
      fontSize: utils.typography.sizes.base,
      fontFamily: utils.typography.body.fontFamily,
    },
    inputError: {
      borderColor: tokens.color.error,
    },
    errorText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.error,
      marginTop: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      fontWeight: utils.typography.weights.semibold,
    },
    hintText: {
      fontSize: utils.typography.sizes.sm,
      color: `${tokens.color.onSurface}99`, // 99 = 60% opacity in hex
      marginTop: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
    },
  });
}

